import { lightTheme } from '@/customTheme'

/** The artwork bitmap paints "50% OFF", so changing this also needs a new image. */
export const LIMITED_OFFER_DISCOUNT_PERCENT = 50

export const LIMITED_OFFER_COUNTDOWN_SECONDS = 60 * 60

/** Settles the close when no animationend fires (reduced motion, background tab). */
export const LIMITED_OFFER_EXIT_GUARD_MS = 260

const CONFETTI_START_MS = 600
const CONFETTI_STAGGER_MS = 22

const CONFETTI_COLORS = {
  red: lightTheme.colors.pink,
  orange: lightTheme.colors.orange100,
  superPurple: lightTheme.colors.purpleVivid,
} as const

interface ConfettiBitSpec {
  /** Flight angle in degrees. */
  angle: number
  /** Flight distance to the top of the arc, in px. */
  distance: number
  color: keyof typeof CONFETTI_COLORS
  width: number
  height: number
  radius: string
  rotation: number
}

/** Fixed table, not random, so the burst is identical on every open. */
const CONFETTI_BITS: readonly ConfettiBitSpec[] = [
  { angle: -108, distance: 211, color: 'red', width: 7, height: 13, radius: '2px', rotation: 180 },
  { angle: -84, distance: 154, color: 'orange', width: 9, height: 9, radius: '50%', rotation: -140 },
  { angle: -62, distance: 128, color: 'superPurple', width: 6, height: 14, radius: '3px', rotation: 220 },
  { angle: -40, distance: 148, color: 'red', width: 10, height: 10, radius: '50%', rotation: -200 },
  { angle: -18, distance: 118, color: 'orange', width: 7, height: 12, radius: '2px', rotation: 160 },
  { angle: -128, distance: 227, color: 'superPurple', width: 8, height: 8, radius: '50%', rotation: -180 },
  { angle: -150, distance: 192, color: 'red', width: 6, height: 15, radius: '3px', rotation: 240 },
  { angle: -168, distance: 221, color: 'orange', width: 8, height: 12, radius: '2px', rotation: -160 },
  { angle: -96, distance: 178, color: 'red', width: 6, height: 11, radius: '2px', rotation: 200 },
  { angle: -72, distance: 104, color: 'superPurple', width: 9, height: 9, radius: '50%', rotation: -120 },
  { angle: -50, distance: 172, color: 'orange', width: 7, height: 14, radius: '3px', rotation: 190 },
  { angle: -140, distance: 266, color: 'red', width: 8, height: 8, radius: '50%', rotation: -220 },
  { angle: -30, distance: 96, color: 'superPurple', width: 6, height: 12, radius: '2px', rotation: 150 },
  { angle: -116, distance: 154, color: 'orange', width: 7, height: 7, radius: '50%', rotation: -170 },
  { angle: -10, distance: 136, color: 'red', width: 7, height: 13, radius: '3px', rotation: 210 },
  { angle: -178, distance: 179, color: 'superPurple', width: 8, height: 11, radius: '2px', rotation: -190 },
  { angle: -78, distance: 196, color: 'orange', width: 6, height: 10, radius: '2px', rotation: 230 },
  { angle: -102, distance: 179, color: 'red', width: 9, height: 9, radius: '50%', rotation: -150 },
]

/** CSS custom properties consumed by the shared confetti keyframes. */
export type ConfettiBitStyle = Record<
  '--dx' | '--dy' | '--rot' | '--c' | '--w' | '--h' | '--r' | '--delay',
  string
>

export const CONFETTI_BIT_STYLES: readonly ConfettiBitStyle[] = CONFETTI_BITS.map((bit, index) => {
  const radians = (bit.angle * Math.PI) / 180
  return {
    '--dx': `${Math.round(Math.cos(radians) * bit.distance)}px`,
    '--dy': `${Math.round(Math.sin(radians) * bit.distance)}px`,
    '--rot': `${bit.rotation}deg`,
    '--c': CONFETTI_COLORS[bit.color],
    '--w': `${bit.width}px`,
    '--h': `${bit.height}px`,
    '--r': bit.radius,
    '--delay': `${CONFETTI_START_MS + index * CONFETTI_STAGGER_MS}ms`,
  }
})

export function formatOfferCountdown(remainingSeconds: number): string {
  const seconds = Math.max(0, Math.floor(remainingSeconds))
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`
}
